import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "../../auth/AuthContext";
import { formatDateBR, formatMoneyBR } from "../../utils/format";
import { PeriodNavigator } from "../../components/PeriodNavigator";
import { formatDateInput, getMonthRange } from "../../utils/dateRange";
import { usePermission } from "../../auth/usePermission";

type PayableRow = {
  id: number;
  description: string;
  paidAmount?: number | null;
  paidAt?: string | null;
  paymentDate?: string | null;
  creditCardTransactionId?: number | null;
  supplier?: { entity?: { name: string } | null } | null;
  costCenter?: { code?: string | null; name: string } | null;
  account?: { code: string; name: string } | null;
  paymentDescription?: string | null;
};

type TransactionRow = {
  id: number;
  occurredAt: string;
  description: string;
  merchant?: string | null;
  amount: number;
  invoiceId?: number | null;
  installmentNumber?: number | null;
  installmentCount?: number | null;
  originType?: string | null;
  idExterno?: string | null;
};

const normalizeText = (value: string) =>
  value
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, "")
    .trim();

const scoreMatch = (payable: PayableRow, tx: TransactionRow) => {
  let score = 0;
  const paid = Number(payable.paidAmount ?? 0);
  if (Math.abs(paid - Number(tx.amount)) < 0.01) {
    score += 60;
  }
  const paidAt = payable.paidAt || payable.paymentDate;
  if (paidAt) {
    const diffDays =
      Math.abs(
        new Date(paidAt).getTime() - new Date(tx.occurredAt).getTime()
      ) /
      (1000 * 60 * 60 * 24);
    if (diffDays <= 2) {
      score += 25;
    }
  }
  const base = normalizeText(payable.description || "");
  const target = normalizeText(tx.description || "");
  if (base && target && (target.includes(base) || base.includes(target))) {
    score += 15;
  }
  return score;
};

const confidenceLabel = (score: number) => {
  if (score >= 70) return { label: "Alta", className: "bg-success" };
  if (score >= 40) return { label: "Media", className: "bg-warning text-dark" };
  return { label: "Baixa", className: "bg-secondary" };
};

export const CreditCardReconcilePage: React.FC = () => {
  const { client } = useAuth();
  const canManage = usePermission("FIN_CC_RECONCILE_MANAGE");

  const [cards, setCards] = useState<Array<{ id: number; name: string }>>([]);
  const [cardId, setCardId] = useState<string>("");
  const [pendingOnly, setPendingOnly] = useState(true);
  const [payables, setPayables] = useState<PayableRow[]>([]);
  const [transactions, setTransactions] = useState<TransactionRow[]>([]);
  const [selectedPayable, setSelectedPayable] = useState<PayableRow | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const monthRange = useMemo(() => getMonthRange(new Date()), []);
  const [fromDate, setFromDate] = useState(formatDateInput(monthRange.from));
  const [toDate, setToDate] = useState(formatDateInput(monthRange.to));

  useEffect(() => {
    const loadCards = async () => {
      const response = await client.get("/modules/cards?take=200&skip=0");
      if (!response.ok) {
        return;
      }
      const payload = (await response.json()) as {
        data: Array<{ id: number; name: string }>;
      };
      setCards(payload.data);
      if (!cardId && payload.data.length > 0) {
        setCardId(String(payload.data[0].id));
      }
    };
    void loadCards();
  }, [client, cardId]);

  const loadData = useCallback(async () => {
    if (!cardId) {
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const query = new URLSearchParams({
        from: fromDate,
        to: toDate,
        pendingOnly: String(pendingOnly),
      });
      const response = await client.get(
        `/modules/credit-cards/${cardId}/reconcile?${query.toString()}`
      );
      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        throw new Error(payload.error || "Falha ao carregar dados.");
      }
      const payload = (await response.json()) as {
        payables: PayableRow[];
        transactions: TransactionRow[];
      };
      setPayables(payload.payables);
      setTransactions(payload.transactions);
      setSelectedPayable(null);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Falha ao carregar dados.");
    } finally {
      setLoading(false);
    }
  }, [cardId, client, fromDate, pendingOnly, toDate]);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  const rankedTransactions = useMemo(() => {
    if (!selectedPayable) {
      return transactions;
    }
    return [...transactions]
      .map((tx) => ({ tx, score: scoreMatch(selectedPayable, tx) }))
      .sort((a, b) => b.score - a.score);
  }, [selectedPayable, transactions]);

  const handleLink = async (transactionId: number) => {
    if (!selectedPayable) {
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const response = await client.post("/modules/credit-cards/reconcile/link", {
        accountPayableId: selectedPayable.id,
        creditCardTransactionId: transactionId,
      });
      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        throw new Error(payload.error || "Falha ao vincular.");
      }
      await loadData();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Falha ao vincular.");
    } finally {
      setLoading(false);
    }
  };

  const handleUnlink = async (payableId: number) => {
    setLoading(true);
    setError(null);
    try {
      const response = await client.post("/modules/credit-cards/reconcile/unlink", {
        accountPayableId: payableId,
      });
      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        throw new Error(payload.error || "Falha ao desvincular.");
      }
      await loadData();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Falha ao desvincular.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container py-4">
      <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-3">
        <h1 className="mb-0">Reconciliacao Cartao</h1>
      </div>

      <div className="card shadow-sm mb-3">
        <div className="card-body">
          <div className="row g-2 align-items-end">
            <div className="col-12 col-md-4">
              <label className="form-label mb-1">Cartao</label>
              <select
                className="form-select"
                value={cardId}
                onChange={(event) => setCardId(event.target.value)}
              >
                <option value="">Selecione...</option>
                {cards.map((card) => (
                  <option key={card.id} value={card.id}>
                    {card.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-12 col-md-3">
              <label className="form-label mb-1">De</label>
              <input
                type="date"
                className="form-control"
                value={fromDate}
                onChange={(event) => setFromDate(event.target.value)}
              />
            </div>
            <div className="col-12 col-md-3">
              <label className="form-label mb-1">Ate</label>
              <input
                type="date"
                className="form-control"
                value={toDate}
                onChange={(event) => setToDate(event.target.value)}
              />
            </div>
            <div className="col-12 col-md-2">
              <PeriodNavigator fromDate={fromDate} toDate={toDate} onChange={(from, to) => {
                setFromDate(from);
                setToDate(to);
              }} />
            </div>
          </div>
          <div className="form-check form-switch mt-3">
            <input
              className="form-check-input"
              type="checkbox"
              checked={pendingOnly}
              onChange={(event) => setPendingOnly(event.target.checked)}
            />
            <label className="form-check-label">Somente pendentes</label>
          </div>
          {error && <div className="alert alert-danger mt-3">{error}</div>}
        </div>
      </div>

      <div className="row g-3">
        <div className="col-12 col-lg-6">
          <div className="card shadow-sm h-100">
            <div className="card-header">
              <strong>Pagamentos pendentes</strong>
            </div>
            <div className="card-body">
              {loading && <div className="alert alert-light">Carregando...</div>}
              <div className="table-responsive">
                <table className="table table-sm align-middle">
                  <thead>
                    <tr>
                      <th>Id</th>
                      <th>Descricao</th>
                      <th>Fornecedor</th>
                      <th>Valor</th>
                      <th>Pagamento</th>
                      <th>Acoes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {payables.map((item) => (
                      <tr key={item.id} className={selectedPayable?.id === item.id ? "table-info" : ""}>
                        <td>{item.id}</td>
                        <td>{item.description}</td>
                        <td>{item.supplier?.entity?.name ?? "-"}</td>
                        <td>{formatMoneyBR(Number(item.paidAmount ?? 0))}</td>
                        <td>{item.paidAt ? formatDateBR(item.paidAt) : "-"}</td>
                        <td className="text-end">
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-primary me-2"
                            onClick={() => setSelectedPayable(item)}
                          >
                            Selecionar
                          </button>
                          {item.creditCardTransactionId && canManage && (
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => handleUnlink(item.id)}
                            >
                              Desvincular
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                    {payables.length === 0 && (
                      <tr>
                        <td colSpan={6} className="text-center">
                          Nenhum pagamento encontrado.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        <div className="col-12 col-lg-6">
          <div className="card shadow-sm h-100">
            <div className="card-header">
              <strong>Transacoes do cartao</strong>
            </div>
            <div className="card-body">
              {selectedPayable && (
                <div className="alert alert-light">
                  Selecionado, {selectedPayable.description}
                </div>
              )}
              <div className="table-responsive">
                <table className="table table-sm align-middle">
                  <thead>
                    <tr>
                      <th>Id</th>
                      <th>Data</th>
                      <th>Descricao</th>
                      <th>Valor</th>
                      <th>Confianca</th>
                      <th>Acoes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(selectedPayable ? rankedTransactions : transactions).map((entry) => {
                      const tx = "tx" in entry ? entry.tx : entry;
                      const score = "tx" in entry ? entry.score : 0;
                      const conf = confidenceLabel(score);
                      return (
                        <tr key={tx.id}>
                          <td>{tx.id}</td>
                          <td>{formatDateBR(tx.occurredAt)}</td>
                          <td>{tx.description}</td>
                          <td>{formatMoneyBR(Number(tx.amount))}</td>
                          <td>
                            <span className={`badge ${conf.className}`}>
                              {conf.label}
                            </span>
                          </td>
                          <td className="text-end">
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-success me-2"
                              disabled={!selectedPayable || !canManage}
                              onClick={() => handleLink(tx.id)}
                            >
                              Vincular
                            </button>
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-secondary"
                              disabled
                              title="Criacao manual em breve"
                            >
                              Criar transacao
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                    {transactions.length === 0 && (
                      <tr>
                        <td colSpan={6} className="text-center">
                          Nenhuma transacao encontrada.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

